import { defineStore } from 'pinia'

export const useBlogStore = defineStore('blog', {
  state: () => ({
    blogs: [],
    loading: false,
    error: null,
    currentBlog: null
  }),
  getters: {
    latestBlogs: (state) => {
      return state.blogs.slice(0, 6) // Get latest 6 blogs
    },
    featuredBlogs: (state) => {
      return state.blogs.filter(blog => blog.featured) || []
    },
    blogsByCategory: (state) => (category) => {
      return state.blogs.filter(blog => blog.category === category) || []
    }
  },
  actions: {
    setBlogs(data) {
      this.blogs = data || []
    },
    setCurrentBlog(blog) {
      this.currentBlog = blog
    },
    setLoading(loading) {
      this.loading = loading
    },
    setError(error) {
      this.error = error
    },
    async fetchBlogs() {
      this.setLoading(true)
      this.setError(null)
      try {
        const response = await fetch('https://www.franchisebrigade.com/admin/api/blog')
        if (!response.ok) {
          throw new Error('Failed to fetch blogs')
        }
        const data = await response.json()
        // Transform API data to match our expected format
        const transformedData = data.map(blog => ({
          id: blog.id,
          title: blog.title,
          slug: blog.slug,
          summary: blog.meta_description,
          content: blog.content,
          image: blog.image,
          category: blog.category,
          featured: blog.is_featured || false,
          created_at: blog.created_at,
          author: blog.author,
          meta_description: blog.meta_description,
          meta_title: blog.meta_title,
          tags: blog.tags || []
        }))
        this.setBlogs(transformedData)
      } catch (error) {
        this.setError(error.message)
        // Fallback to mock data if API fails
        this.setBlogs(this.getMockBlogs())
      } finally {
        this.setLoading(false)
      }
    },
    async fetchBlogBySlug(slug) {
      this.setLoading(true)
      this.setError(null)
      try {
        const response = await fetch(`https://www.franchisebrigade.com/admin/api/blog/${slug}`)
        if (!response.ok) {
          throw new Error('Failed to fetch blog')
        }
        const data = await response.json()
        // Transform API data to match our expected format
        const transformedBlog = {
          id: data.id,
          title: data.title,
          slug: data.slug,
          summary: data.meta_description,
          content: data.content,
          image: data.image,
          category: data.category,
          featured: data.is_featured || false,
          created_at: data.created_at,
          author: data.author,
          meta_description: data.meta_description,
          meta_title: data.meta_title,
          tags: data.tags || []
        }
        this.setCurrentBlog(transformedBlog)
      } catch (error) {
        this.setError(error.message)
        // Fallback to mock data if API fails
        const mockBlog = this.getMockBlogs().find(blog => blog.slug === slug)
        this.setCurrentBlog(mockBlog)
      } finally {
        this.setLoading(false)
      }
    },
    getMockBlogs() {
      return [
        {
          id: 1,
          title: 'How to Choose the Right Franchise',
          slug: 'how-to-choose-the-right-franchise',
          summary: 'Tips and strategies for selecting a franchise that matches your goals and budget.',
          content: 'Choosing the right franchise is one of the most important decisions you\'ll make in your entrepreneurial journey. This comprehensive guide will help you evaluate different franchise opportunities and make an informed decision.',
          image: 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=400&q=80',
          category: 'Franchise Guide',
          featured: true,
          created_at: '2025-01-15',
          author: 'Franchise Brigade Team',
          meta_description: 'Learn the essential steps to choose the perfect franchise for your business goals and budget.',
          meta_title: 'How to Choose the Right Franchise',
          tags: ['franchise', 'business', 'guide']
        },
        {
          id: 2,
          title: 'Top 5 Food Franchises in India',
          slug: 'top-5-food-franchises-in-india',
          summary: 'A rundown of the most popular and profitable food franchise opportunities.',
          content: 'The food franchise sector in India is booming, with numerous opportunities for entrepreneurs. Here are the top 5 food franchises that are making waves in the Indian market.',
          image: 'https://images.unsplash.com/photo-1519125323398-675f0ddb6308?auto=format&fit=crop&w=400&q=80',
          category: 'Food & Beverage',
          featured: true,
          created_at: '2025-01-10',
          author: 'Franchise Brigade Team',
          meta_description: 'Discover the most profitable food franchise opportunities in India\'s growing market.',
          meta_title: 'Top 5 Food Franchises in India',
          tags: ['food', 'franchise', 'india']
        },
        {
          id: 3,
          title: 'Franchise vs. Startup: Pros and Cons',
          slug: 'franchise-vs-startup-pros-and-cons',
          summary: 'Should you start your own business or buy a franchise? We break down the differences.',
          content: 'When considering entrepreneurship, you have two main paths: starting your own business or buying a franchise. Each option has its advantages and challenges.',
          image: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?auto=format&fit=crop&w=400&q=80',
          category: 'Business Strategy',
          featured: false,
          created_at: '2025-01-05',
          author: 'Franchise Brigade Team',
          meta_description: 'Compare the pros and cons of franchising versus starting your own business from scratch.',
          meta_title: 'Franchise vs. Startup: Pros and Cons',
          tags: ['business', 'startup', 'franchise']
        },
        {
          id: 4,
          title: 'Retail Franchise Trends 2025',
          slug: 'retail-franchise-trends-2025',
          summary: 'What\'s hot in the retail franchise sector for the coming year.',
          content: 'The retail franchise landscape is constantly evolving. Here are the key trends that will shape the industry in 2025 and beyond.',
          image: 'https://images.unsplash.com/photo-1465101046530-73398c7f28ca?auto=format&fit=crop&w=400&q=80',
          category: 'Retail',
          featured: false,
          created_at: '2025-01-01',
          author: 'Franchise Brigade Team',
          meta_description: 'Stay ahead of the curve with the latest retail franchise trends for 2025.',
          meta_title: 'Retail Franchise Trends 2025',
          tags: ['retail', 'trends', '2025']
        },
        {
          id: 5,
          title: 'How to Finance Your Franchise',
          slug: 'how-to-finance-your-franchise',
          summary: 'Explore funding options and tips for securing capital for your franchise.',
          content: 'Securing financing for your franchise is a crucial step in the process. Learn about various funding options and strategies to get the capital you need.',
          image: 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=400&q=80',
          category: 'Finance',
          featured: false,
          created_at: '2024-12-28',
          author: 'Franchise Brigade Team',
          meta_description: 'Comprehensive guide to financing options for franchise investments.',
          meta_title: 'How to Finance Your Franchise',
          tags: ['finance', 'franchise', 'funding']
        },
        {
          id: 6,
          title: 'Franchise Success Stories',
          slug: 'franchise-success-stories',
          summary: 'Inspiring stories from successful franchise owners across India.',
          content: 'Real stories from franchise owners who have achieved success in their business ventures. Learn from their experiences and insights.',
          image: 'https://images.unsplash.com/photo-1464983953574-0892a716854b?auto=format&fit=crop&w=400&q=80',
          category: 'Success Stories',
          featured: true,
          created_at: '2024-12-25',
          author: 'Franchise Brigade Team',
          meta_description: 'Get inspired by real success stories from franchise owners across India.',
          meta_title: 'Franchise Success Stories',
          tags: ['success', 'stories', 'inspiration']
        }
      ]
    }
  }
}) 